// Account sync planning (docs/tune-library-plan.md §4, step C): what moves
// between this device and your account, decided from three facts per item —
// is it here, is it in the account, and was it in both at the last sync.
//
// The last-synced state is what makes a deletion distinguishable from a
// never-uploaded item: a tune the account has and this device does not was
// either deleted here (push the deletion) or saved on another device (pull
// it), and only the memory of the last sync can tell which. Everything here is
// pure; the engine (src/data/accountSync.ts) fetches, applies and remembers.
//
// Two kinds of thing sync, and they behave differently because they ARE
// different:
//
// * SAVED TUNES are snapshots — a tune is saved once (Save a copy mints a new
//   id every time) and deleted once, never edited in place, so there is no
//   content conflict to resolve: it is a set union, plus deletions. A tune
//   saved on this device before it was linked to the account is DEVICE-ONLY
//   until you choose to upload (the device may be shared; your tunes are not
//   someone else's to publish), and a local tune only counts as deleted when
//   you deleted it (a tombstone) — never because a guard dropped it.
// * TUNE SETTINGS (band, feel mapping, voicings and pins, tempo/key/mix) are
//   edited all the time, on either device, so each tune's record is merged
//   three ways by hash: whichever side changed since the last sync wins, and
//   when both did, the later change wins. On a device's first sync, a tune
//   whose settings differ on both sides takes the account's: your settings
//   follow you to a new device, which is the point.

import type { StyleBand } from '@/music/style';
import type { TuneArrangement } from '@/music/voicingPins';
import { contentHash } from '@/storage/documentSchema';
import type { TunePlaybackRecord } from './tunePlayback';

// --- saved tunes ------------------------------------------------------------

export interface TuneSyncInput {
  /** Saved tune ids on this device. */
  local: readonly string[];
  /** Saved tune ids in the account. */
  remote: readonly string[];
  /** Ids that were in both at the last sync. */
  synced: readonly string[];
  /** Ids you deleted on this device since (tombstones). */
  deleted: readonly string[];
  /** Whether device-only tunes go up (you said yes, or there were none). */
  uploadTunes: boolean;
}

export interface TuneSyncPlan {
  /** Device tunes to upload. */
  push: string[];
  /** Account tunes to bring down. */
  pull: string[];
  /** Tunes you deleted here, to delete from the account. */
  deleteRemote: string[];
  /** Tunes deleted on another device, to delete here. */
  deleteLocal: string[];
  /** Device tunes waiting for you to say they may go up. */
  deviceOnly: string[];
  /** The synced set once the plan has been carried out. */
  synced: string[];
}

export function planTuneSync(input: TuneSyncInput): TuneSyncPlan {
  const local = new Set(input.local);
  const remote = new Set(input.remote);
  const synced = new Set(input.synced);
  const deleted = new Set(input.deleted);
  const plan: TuneSyncPlan = {
    push: [],
    pull: [],
    deleteRemote: [],
    deleteLocal: [],
    deviceOnly: [],
    synced: [],
  };
  for (const id of local) {
    if (remote.has(id)) plan.synced.push(id);
    else if (synced.has(id)) plan.deleteLocal.push(id);
    else if (input.uploadTunes) {
      plan.push.push(id);
      plan.synced.push(id);
    } else plan.deviceOnly.push(id);
  }
  for (const id of remote) {
    if (local.has(id)) continue;
    // Deleted here only if YOU deleted it; a tune that went missing any
    // other way (a guard dropped a corrupt copy) comes back from the account.
    if (synced.has(id) && deleted.has(id)) plan.deleteRemote.push(id);
    else {
      plan.pull.push(id);
      plan.synced.push(id);
    }
  }
  return plan;
}

// --- tune settings ----------------------------------------------------------

/** How you play one tune, as it travels. */
export interface TuneSettingsRecord {
  band?: StyleBand;
  feels?: Record<string, StyleBand>;
  arrangement?: TuneArrangement;
  playback?: TunePlaybackRecord;
}

/** The four per-tune maps the store keeps. */
export interface TuneSettingsMaps {
  tuneBands: Record<string, StyleBand>;
  tuneFeels: Record<string, Record<string, StyleBand>>;
  tuneArrangements: Record<string, TuneArrangement>;
  tunePlayback: Record<string, TunePlaybackRecord>;
}

const isEmpty = (value: object | undefined) => !value || Object.keys(value).length === 0;

/** One tune's record out of the maps; null when you have changed nothing. */
export function settingsRecordOf(maps: TuneSettingsMaps, key: string): TuneSettingsRecord | null {
  const record: TuneSettingsRecord = {};
  if (maps.tuneBands[key]) record.band = maps.tuneBands[key];
  if (!isEmpty(maps.tuneFeels[key])) record.feels = maps.tuneFeels[key];
  const arrangement = maps.tuneArrangements[key];
  if (arrangement && (arrangement.voicing || !isEmpty(arrangement.pins))) {
    record.arrangement = arrangement;
  }
  // A record holding only the house revision it was made against is not a
  // change anybody made.
  const playback = maps.tunePlayback[key];
  if (playback && Object.keys(playback).some((k) => k !== 'basedOn')) record.playback = playback;
  return isEmpty(record) ? null : record;
}

/** Every tune key with a record. */
export function settingsKeysOf(maps: TuneSettingsMaps): string[] {
  const keys = new Set([
    ...Object.keys(maps.tuneBands),
    ...Object.keys(maps.tuneFeels),
    ...Object.keys(maps.tuneArrangements),
    ...Object.keys(maps.tunePlayback),
  ]);
  return [...keys].filter((key) => settingsRecordOf(maps, key) !== null).sort();
}

/** JSON with keys sorted at every depth, so equal records serialise equally. */
export function stableStringify(value: unknown): string {
  return JSON.stringify(sortKeys(value));
}

function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (!value || typeof value !== 'object') return value;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(value).sort()) {
    const inner = (value as Record<string, unknown>)[key];
    if (inner !== undefined) out[key] = sortKeys(inner);
  }
  return out;
}

/** A record's identity for the merge; '' for no record. */
export function recordHash(record: unknown): string {
  return record ? contentHash(stableStringify(record)) : '';
}

export interface SettingsSyncInput {
  /** Hash of each tune's record on this device. */
  local: Record<string, string>;
  /** Hash and last write of each tune's record in the account. */
  remote: Record<string, { hash: string; updatedAt: string }>;
  /** Hash of each record at the last sync (both sides agreed then). */
  synced: Record<string, string>;
  /** When this device last changed a record, if it saw it happen (ms). */
  localChangedAt?: Record<string, number>;
  /** Now, for a local change nobody saw happen (ms). */
  now: number;
}

export interface SettingsSyncPlan {
  /** Records to write to the account. */
  push: string[];
  /** Records to delete from the account (you reset the tune here). */
  pushDelete: string[];
  /** Records to take from the account. */
  pull: string[];
  /** Records to drop here (reset on another device). */
  pullDelete: string[];
  /** The synced hashes once the plan has been carried out. */
  synced: Record<string, string>;
}

export function planSettingsSync(input: SettingsSyncInput): SettingsSyncPlan {
  const plan: SettingsSyncPlan = { push: [], pushDelete: [], pull: [], pullDelete: [], synced: {} };
  const keys = new Set([
    ...Object.keys(input.local),
    ...Object.keys(input.remote),
    ...Object.keys(input.synced),
  ]);
  for (const key of [...keys].sort()) {
    const l = input.local[key] ?? '';
    const r = input.remote[key]?.hash ?? '';
    const s = input.synced[key];
    const settle = (hash: string) => {
      if (hash) plan.synced[key] = hash;
    };
    const takeLocal = () => {
      if (l) plan.push.push(key);
      else plan.pushDelete.push(key);
      settle(l);
    };
    const takeRemote = () => {
      if (r) plan.pull.push(key);
      else plan.pullDelete.push(key);
      settle(r);
    };
    if (l === r) {
      settle(l);
      continue;
    }
    if (s === undefined) {
      // Never synced from this device: whichever side has it; on both,
      // the account's (your settings follow you to a new device).
      if (!r) takeLocal();
      else takeRemote();
      continue;
    }
    const localChanged = l !== s;
    const remoteChanged = r !== s;
    if (localChanged && !remoteChanged) takeLocal();
    else if (remoteChanged && !localChanged) takeRemote();
    else {
      const localAt = input.localChangedAt?.[key] ?? input.now;
      const remoteAt = Date.parse(input.remote[key]?.updatedAt ?? '') || 0;
      if (remoteAt > localAt) takeRemote();
      else takeLocal();
    }
  }
  return plan;
}

// --- what this device remembers ---------------------------------------------

/** What this device last agreed with one account about your play history and
 * loves (migration 0018). */
export interface PersonalSyncMemory {
  /** This device's history as last written to its row (its hash). */
  history?: string;
  /** Your other devices' history rows as last read: device id → updated_at. */
  historyDevices?: Record<string, string>;
  /** Your loves as last agreed: the row's revision, and the doc's hash. */
  tasteRevision?: number;
  taste?: string;
}

/** What this device last agreed with one account. */
export interface AccountSyncState extends PersonalSyncMemory {
  /** The account it agreed with: another user signing in starts afresh. */
  userId: string;
  /** Saved tune ids in both at the last sync. */
  tunes: string[];
  /** Each tune record's hash at the last sync. */
  settings: Record<string, string>;
  /** Device-only tunes may go up (you said yes, or there were none). */
  uploadTunes: boolean;
  lastSyncedAt: string | null;
}

/** The history and loves half of a sync memory. */
export function personalMemoryOf(state: PersonalSyncMemory | null): PersonalSyncMemory {
  if (!state) return {};
  return {
    ...(state.history !== undefined ? { history: state.history } : {}),
    ...(state.historyDevices !== undefined ? { historyDevices: state.historyDevices } : {}),
    ...(state.tasteRevision !== undefined ? { tasteRevision: state.tasteRevision } : {}),
    ...(state.taste !== undefined ? { taste: state.taste } : {}),
  };
}

/** The persisted state, guarded field by field (the rehydrate path). */
export function normalizeAccountSync(raw: unknown): AccountSyncState | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.userId !== 'string' || !r.userId) return null;
  const tunes = Array.isArray(r.tunes)
    ? r.tunes.filter((id): id is string => typeof id === 'string')
    : [];
  const settings: Record<string, string> = {};
  if (r.settings && typeof r.settings === 'object') {
    for (const [key, hash] of Object.entries(r.settings as Record<string, unknown>)) {
      if (typeof hash === 'string' && hash) settings[key] = hash;
    }
  }
  const historyDevices: Record<string, string> = {};
  if (r.historyDevices && typeof r.historyDevices === 'object') {
    for (const [id, at] of Object.entries(r.historyDevices as Record<string, unknown>)) {
      if (typeof at === 'string') historyDevices[id] = at;
    }
  }
  const revision = r.tasteRevision;
  return {
    userId: r.userId,
    tunes,
    settings,
    uploadTunes: r.uploadTunes === true,
    lastSyncedAt: typeof r.lastSyncedAt === 'string' ? r.lastSyncedAt : null,
    ...(typeof r.history === 'string' ? { history: r.history } : {}),
    ...(r.historyDevices && typeof r.historyDevices === 'object' ? { historyDevices } : {}),
    ...(typeof revision === 'number' && Number.isInteger(revision) && revision >= 0
      ? { tasteRevision: revision }
      : {}),
    ...(typeof r.taste === 'string' ? { taste: r.taste } : {}),
  };
}
